import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { AuthContext } from '../providers/AuthProvider';
import assistantService from '../services/api/assistantService';

const AssistantContext = createContext(null);

export const AssistantProvider = ({ children }) => {
  const { user } = useContext(AuthContext);
  const [assistants, setAssistants] = useState([]);
  const [selectedAssistant, setSelectedAssistant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAssistants = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const response = await assistantService.getAssistants();
      const list = response.data?.map(a => ({ id: a.id, ...a.attributes })) || [];
      setAssistants(list);
      setError(null);

      // Restore from localStorage or use default
      const stored = localStorage.getItem('@sacgpt:selectedAssistant');
      if (stored) {
        const parsed = JSON.parse(stored);
        const found = list.find(a => String(a.id) === String(parsed.id));
        if (found) {
          setSelectedAssistant(found);
        } else {
          selectDefault(list);
        }
      } else {
        selectDefault(list);
      }
    } catch (error) {
      console.error('Error fetching assistants:', error);
      setError(error);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  const selectDefault = (list) => {
    const defaultAssistant = list.find(a => a.is_default) || list[0];
    if (defaultAssistant) {
      setSelectedAssistant(defaultAssistant);
      localStorage.setItem('@sacgpt:selectedAssistant', JSON.stringify(defaultAssistant));
    }
  };

  const selectAssistant = useCallback((assistant) => {
    setSelectedAssistant(assistant);
    localStorage.setItem('@sacgpt:selectedAssistant', JSON.stringify(assistant));
  }, []);

  useEffect(() => {
    // Only fetch after user is authenticated — prevents empty list on first load
    if (user?.id) {
      fetchAssistants();
    }
  }, [user?.id, fetchAssistants]);

  // Mantém os assistentes (e o balão de "cliente esperando" no Escritório 3D) em
  // tempo real: o AttendantProvider já reemite os eventos de takeover no window —
  // a mesma fonte que faz a sidebar piscar. Aqui coalescemos a rajada (debounce)
  // e refazemos um fetch silencioso, então o balão do robô aparece/some na hora.
  useEffect(() => {
    if (!user?.id) return undefined;

    let timer = null;
    const scheduleRefresh = () => {
      clearTimeout(timer);
      timer = setTimeout(() => fetchAssistants({ silent: true }), 500);
    };

    const EVENTS = [
      'new_takeover_request',
      'chats_updated',
      'takeover_accepted',
      'takeover_completed',
      'takeover_returned_to_bot',
    ];
    EVENTS.forEach((ev) => window.addEventListener(ev, scheduleRefresh));

    return () => {
      clearTimeout(timer);
      EVENTS.forEach((ev) => window.removeEventListener(ev, scheduleRefresh));
    };
  }, [user?.id, fetchAssistants]);

  const contextValue = useMemo(() => ({
    assistants,
    selectedAssistant,
    loading,
    error,
    selectAssistant,
    refreshAssistants: fetchAssistants,
  }), [assistants, selectedAssistant, loading, error, selectAssistant, fetchAssistants]);

  return (
    <AssistantContext.Provider value={contextValue}>
      {children}
    </AssistantContext.Provider>
  );
};

export const useAssistantContext = () => {
  const ctx = useContext(AssistantContext);
  if (!ctx) {
    console.warn('useAssistantContext called outside of AssistantProvider. Using fallback values.');
    return {
      assistants: [],
      selectedAssistant: null,
      loading: true,
      error: null,
      selectAssistant: () => {},
      refreshAssistants: () => {},
    };
  }
  return ctx;
};
